import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { Button, Icon, Intent } from '@blueprintjs/core';
import { observer, inject } from 'mobx-react';
import { observable, toJS } from 'mobx';
import moment from 'moment';
import * as DataList from 'components/DataList';
import Toast from 'helpers/toaster';
import { setLicenseKey } from 'survey-core';
import { SurveyCreator, SurveyCreatorComponent } from 'survey-creator-react';
import { editorLocalization, registerCreatorTheme } from 'survey-creator-core';
import { surveyCreatorFieldNexus } from '../../themes/survey-creator-fieldnexus';

import 'survey-core/survey-core.min.css';
import 'survey-creator-core/survey-creator-core.min.css';

// SurveyJS shows a licence notice in the builder until the company's licence
// key is set (REACT_APP_SURVEYJS_LICENSE_KEY in .env).
if (process.env.REACT_APP_SURVEYJS_LICENSE_KEY) {
  setLicenseKey(process.env.REACT_APP_SURVEYJS_LICENSE_KEY);
}

registerCreatorTheme(surveyCreatorFieldNexus);

// The builder's English text says "survey"; in Field Nexus these are forms.
// Links (help URLs) are left alone.
export const surveyToForm = (text) =>
  text
    .replace(/\bSurveys\b/g, 'Forms')
    .replace(/\bsurveys\b/g, 'forms')
    .replace(/\bSurvey\b/g, 'Form')
    .replace(/\bsurvey\b/g, 'form');

const renameSurveyToForm = (strings) => {
  Object.keys(strings).forEach((key) => {
    const value = strings[key];
    if (typeof value === 'string' && !/^https?:/.test(value)) {
      strings[key] = surveyToForm(value);
    } else if (value && typeof value === 'object') {
      renameSurveyToForm(value);
    }
  });
};

const translations = editorLocalization.getLocale('en');
renameSurveyToForm(translations);
translations.pe.survey.title = 'Form title';
translations.pe.surveyTitlePlaceholder = 'Form title';

// Saving is the page header's job (one Save form button), so the builder's own
// Save button is hidden and nothing saves by itself.
// The JSON editor is for developers, so it isn't offered.
const creatorOptions = {
  autoSaveEnabled: false,
  collapseOnDrag: false,
  showJSONEditorTab: false,
};

// A form's questions, edited in the SurveyJS builder (/dashboard/forms/:id):
// a header bar (back to Forms, the form's name, whether it's saved, Save
// form) over the builder, which gets the rest of the window.
@inject('formStore', 'viewStore') @observer
class FormsShowPage extends Component {
  @observable dirty = false; // changes since the last save
  @observable savedAt = null; // when this visit last saved
  @observable saveFailed = false;

  creator = null;
  loadedFormId = null;

  componentDidMount() {
    this.creator = new SurveyCreator(creatorOptions);
    this.creator.showSaveButton = false;
    this.creator.applyCreatorTheme(surveyCreatorFieldNexus);
    this.creator.saveSurveyFunc = this.onFormSave;
    this.creator.onModified.add(() => {
      this.dirty = true;
      this.saveFailed = false;
    });
    window.addEventListener('beforeunload', this.warnIfUnsaved);
    this.forceUpdate(); // the builder exists now; render it if the form is here
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.warnIfUnsaved);
    this.props.formStore.clearSelectedForm();
  }

  // Leaving (closing the tab, reloading) with unsaved changes asks first.
  warnIfUnsaved = (event) => {
    if (!this.dirty) return undefined;
    event.preventDefault();
    event.returnValue = '';
    return '';
  };

  // Puts the loaded form into the builder once per form (not on every render,
  // which would throw away edits).
  syncCreator(object) {
    if (!this.creator || this.loadedFormId === object.id) return;
    this.loadedFormId = object.id;
    this.creator.JSON = toJS(object.attributes.schema_version) || {};
    if (this.dirty) this.dirty = false;
  }

  save = () => {
    if (this.creator && !this.props.formStore.isSavingForm) this.creator.saveSurvey();
  };

  // Called by the builder: send the questions, and tell it whether that worked.
  onFormSave = (saveNo, callback) => {
    const { formStore } = this.props;
    const { object } = formStore.form;
    formStore.updateForm({ uuid: object.id, schema_version: this.creator.text })
      .then(() => {
        callback(saveNo, true);
        this.dirty = false;
        this.saveFailed = false;
        this.savedAt = moment();
        Toast.show({ message: 'Form saved.', intent: Intent.SUCCESS });
      })
      .catch((error) => {
        callback(saveNo, false);
        this.saveFailed = true;
        const data = error && error.response && error.response.data;
        const reason = data && (data.error || data.errors);
        Toast.show({
          message: reason ? (Array.isArray(reason) ? reason.join(', ') : reason) : "Couldn't save the form. Try again.",
          intent: Intent.DANGER,
        });
      });
  };

  retry = () => {
    const uuid = window.location.pathname.split('/').filter(Boolean).pop();
    this.props.formStore.loadForm({ uuid });
  };

  status() {
    if (this.props.formStore.isSavingForm) return { tone: 'saving', text: 'Saving…' };
    if (this.saveFailed) return { tone: 'error', text: 'Not saved. Try again.' };
    if (this.dirty) return { tone: 'unsaved', text: 'Unsaved changes' };
    if (this.savedAt) return { tone: 'saved', text: `Saved at ${this.savedAt.format('h:mm A')}` };
    return { tone: 'idle', text: 'No changes yet' };
  }

  renderHeader(object) {
    const { formStore } = this.props;
    const status = this.status();
    return (
      <header className="fe-head">
        <a className="fe-head__back" href="/dashboard/forms">
          <Icon icon="arrow-left" iconSize={14} />
          <span>Forms</span>
        </a>
        <div className="fe-head__title">
          <h1>{object.attributes.name}</h1>
          {object.attributes.id && <span className="fe-head__number">Form {object.attributes.id}</span>}
        </div>
        <div className="fe-head__actions">
          <span className={`fe-status fe-status--${status.tone}`} role="status" aria-live="polite">
            <span className="fe-status__dot" aria-hidden="true" />
            {status.text}
          </span>
          <Button
            className="fe-save"
            intent={Intent.PRIMARY}
            icon="floppy-disk"
            text="Save form"
            loading={formStore.isSavingForm}
            disabled={!this.dirty && !this.saveFailed}
            onClick={this.save}
          />
        </div>
      </header>
    );
  }

  render() {
    const { formStore, viewStore } = this.props;
    const { isLoading, isError, object } = formStore.form;
    let title = 'Form | Field Nexus';
    let content = null;

    if (object && object.id) {
      title = `${object.attributes.name} | Field Nexus`;
      this.syncCreator(object);
      content = (
        <React.Fragment>
          {this.renderHeader(object)}
          <div className="fe-builder">
            {this.creator && <SurveyCreatorComponent creator={this.creator} />}
          </div>
        </React.Fragment>
      );
    } else if (isError && !isLoading) {
      title = "Couldn't load form | Field Nexus";
      content = (
        <div className="fe-error">
          <DataList.Notice
            tone="error"
            icon="error"
            role="alert"
            title="This form couldn't be loaded"
            message="It may have been deleted, or something went wrong on our side."
            actions={[
              { label: 'Try again', icon: 'refresh', onClick: this.retry, primary: true },
              { label: 'Back to forms', icon: 'arrow-left', onClick: () => viewStore.forms() },
            ]}
          />
        </div>
      );
    } else if (isLoading) {
      title = 'Loading..';
      content = (
        <div className="fe-loading" role="status" aria-label="Loading form">
          <div className="fe-head" aria-hidden="true">
            <span className="dl-bone" style={{ width: 70, height: 14 }} />
            <span className="dl-bone" style={{ width: 240, height: 20 }} />
            <span className="dl-bone" style={{ width: 120, height: 36, borderRadius: 8, marginLeft: 'auto' }} />
          </div>
          <div className="fe-loading__body" aria-hidden="true">
            <span className="dl-bone" style={{ width: 220, height: '100%' }} />
            <span className="dl-bone" style={{ flex: 1, height: '100%' }} />
            <span className="dl-bone" style={{ width: 300, height: '100%' }} />
          </div>
        </div>
      );
    }

    return (
      <div className="fe">
        <Helmet>
          <title>{title}</title>
        </Helmet>
        {content}
      </div>
    );
  }
}

export default FormsShowPage;
